import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import { LayoutDashboard, ShoppingBag, Package, MessageSquare, LogOut, Settings as SettingsIcon, FolderTree, Layers, Menu, X, Tag } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { Logo } from "../../components/Logo";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, color: "bg-mint-deep" },
  { to: "/admin/orders", label: "Pedidos", icon: ShoppingBag, color: "bg-yolk" },
  { to: "/admin/products", label: "Productos", icon: Package, color: "bg-cyan-brand" },
  { to: "/admin/sections", label: "Secciones", icon: Layers, color: "bg-mint-deep" },
  { to: "/admin/categories", label: "Categorías", icon: FolderTree, color: "bg-yolk" },
  { to: "/admin/quotes", label: "Cotizaciones", icon: MessageSquare, color: "bg-cyan-brand" },
  { to: "/admin/discounts", label: "Descuentos", icon: Tag, color: "bg-yolk" },
  { to: "/admin/settings", label: "Ajustes", icon: SettingsIcon, color: "bg-mint-deep" },
];

const AdminLayout = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-mint-light relative" data-testid="admin-layout">
      <div className="fixed inset-0 confetti opacity-50 pointer-events-none" />

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 bg-brand-ink text-white border-b-2 border-yolk flex items-center justify-between px-4 h-16">
        <Logo size="small" />
        <button onClick={() => setMobileOpen(true)} data-testid="admin-mobile-toggle" className="h-10 w-10 flex items-center justify-center rounded-xl bg-yolk text-white">
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {/* Mobile backdrop */}
      {mobileOpen && <div onClick={() => setMobileOpen(false)} className="lg:hidden fixed inset-0 z-40 bg-black/50" />}

      <aside
        className={`fixed top-0 left-0 z-50 h-full w-72 bg-brand-ink text-white border-r-4 border-yolk flex flex-col transform transition-transform duration-300
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 lg:w-64`}
      >
        <div className="p-4 border-b-2 border-white/15 flex items-center justify-between gap-2 bg-white">
          <Logo size="large" />
          <button onClick={() => setMobileOpen(false)} className="lg:hidden h-9 w-9 flex items-center justify-center rounded-full bg-mint-light">
            <X className="h-4 w-4" />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1.5 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm transition rounded-xl ${
                  isActive
                    ? `${n.color} text-white font-bold shadow-[3px_3px_0_0_#EC4899]`
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t-2 border-white/15">
          <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout-btn" className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/70 hover:bg-yolk hover:text-white rounded-xl transition">
            <LogOut className="h-4 w-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      <main className="relative lg:ml-64 p-4 sm:p-6 md:p-8 lg:p-10 overflow-x-hidden z-10">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
